import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from '@scramble/ui';
import { MODAL_IDS } from '@/kit/Modal/keys';
import { useModal } from '@/providers/modal';
import { EarlyReservationModal } from '@/modals/EarlyReservationModal';
import { useEarlyReservation } from '@/hooks/shared/useEarlyReservation';
import { prettyDate } from '@/helpers/utils';
import { APP_LINKS } from '@/helpers/links';
import { prettyMoney } from '@scramble/utils';
import {
  DATE_FORMAT_D_MMMM_YYYY,
} from '@/helpers/common';
import IconHelp from '@scramble/design-system/icons/help-circle.svg'
import s from './index.module.scss';

export const EarlyReservationModerate = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { limits, reservedAmount, isReservationFullForNextRound, isReservationFullAndBecomeAvailable, isLoading } = useEarlyReservation();

  if (isLoading || !limits?.reservation_time_left) return null;

  return (
    <>
      <div className={s.wrapper}>
        <div className={s.header}>
          <div className={s.title}>
            {t('investing:early_reservation.title')}
          </div>
          <Button
            theme="neutral"
            appearance="tonal"
            iconUrl={IconHelp}
            onClick={() => navigate(APP_LINKS.loyaltyProgram)}
            size="md"
          />
        </div>
        {isReservationFullForNextRound ? (
          <ReservationFullForNextRound />
        ) : isReservationFullAndBecomeAvailable ? (
          <ReservationFullAnBecomeAvailable />
        ) : (
          <ReservationAvailable reservedAmount={reservedAmount} />
        )}
      </div>
    </>
  );
};

const ReservationAvailable = () => {
  const { t } = useTranslation();
  const {
    status,
    limits,
    reservedAmount,
  } = useEarlyReservation();

  const { openSingleModal } = useModal();

  const onReserve = () => {
    openSingleModal(MODAL_IDS.early_reservation_modal);
  };

 return (
   <div className={s.block}>
     <div className={s.left}>
       <div className="f-body-md f-500">
         {t('investing:early_reservation.reserve_until', {
           date: prettyDate(status.start_date, DATE_FORMAT_D_MMMM_YYYY),
         })}
       </div>
       <div className="f-body-sm">
         {t('investing:early_reservation.your_available_once')}
       </div>
     </div>
     <div className={s.right}>
       {reservedAmount ? (
         <div className="f-body-sm f-500">
           {t('investing:early_reservation.reserved_amount_sum', {
             amount: prettyMoney(reservedAmount),
           })}
         </div>
       ) : (
         <div className="f-body-sm f-500">
           {t('investing:early_reservation.currently_available', {
             amount: prettyMoney(limits?.available_reserve),
           })}
         </div>
       )}

       {reservedAmount ? (
         <Button
           theme="neutral"
           appearance="filled"
           size="md"
           label={t('investing:early_reservation.update')}
           onClick={onReserve}
         />
       ) : (
         <Button
           theme="brand"
           appearance="filled"
           size="md"
           disabled={!limits?.available_reserve}
           label={t('investing:early_reservation.reserve')}
           onClick={onReserve}
         />
       )}
     </div>

     <EarlyReservationModal />
   </div>
 );
}

const ReservationFullAnBecomeAvailable = () => {
  const { t } = useTranslation();
  const { status, limits } =
    useEarlyReservation();

  return (
    <div className={s.block}>
      <div className={s.left} style={{ maxWidth: '610px' }}>
        <div className="f-body-md f-500">
          {t('investing:early_reservation.reserve_until', {
            date: prettyDate(status.start_date, DATE_FORMAT_D_MMMM_YYYY),
          })}
        </div>
        <div className="f-body-sm">
          {t('investing:early_reservation.reservation_currently_full', {
            time: '12:00',
            date: prettyDate(
              limits.next_available_reserve_opens_at,
              DATE_FORMAT_D_MMMM_YYYY,
            ),
          })}
        </div>
      </div>
      <div className={s.right}>
        <div className="f-body-sm f-500">
          {t('investing:early_reservation.next_available', {
            amount: prettyMoney(limits.next_available_reserve),
          })}
        </div>
      </div>
    </div>
  );
}

const ReservationFullForNextRound = () => {
  const { t } = useTranslation();
  return (
    <div className={s.block}>
      <div className={s.left} style={{ maxWidth: '610px' }}>
        <div className="f-body-md f-500">
          {t('investing:early_reservation.reservation_full')}
        </div>
        <div className="f-body-sm">
          {t('investing:early_reservation.reservation_full_desc')}
        </div>
      </div>
    </div>
  );
};

